import { useEffect, useRef, useState } from "react";
import "./OrdersRecoveryTable.scss";
import orderEmptyImg from "../../../assets/images/empty_order_pipeline.svg";
import productListEmptyImg from "../../../assets/images/empty_product_list_default.svg";
import EmptyOrder from "../../EmptyOrder";
import NoSearchResult from "../../NoSearchResult/NoSearchResult";
import Loader from "../../Loader/Loader";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch, RootState } from "../../../store";
import { fetchSkuConfigData } from "../../../store/slices/CTDBSkuConfigSlice";
import {
  ERROR_RETRIVING_DATA,
  NO_DATA_FOR_SKU,
  NO_DRAFT_RECOVERY_HEADING,
  NO_RECOVERY_TEXT,
  TRY_AGAIN,
} from "../../../Constant";
import { MotifButton, MotifTruncate } from "@ey-xd/motif-react";
import { containsEmoji } from "../../../utils/common";

export interface RecoveryColumnConfig {
  key: string;
  label: string;
  render?: (row: any) => React.ReactNode;
}

interface OrdersRecoveryTableProps {
  data: any[];
  columnsData: RecoveryColumnConfig[];
  setOrdersRecoveries: React.Dispatch<React.SetStateAction<any[]>>;
  setShowPagination: React.Dispatch<React.SetStateAction<boolean>>;
  searchText: string;
  isLoading: boolean;
  billingType: string;
  transactionDate: string;
  isFilterApplied: boolean;
  isRecoveriesDetailsLoading: boolean;
  isRecoveriesDetailsError: boolean;
  recoveryDetailsErrorMap: Record<string, string>;
  handleRecoveryRowExpand: (
    SkuId: string,
    OrderParentId: number,
    isRefresh?: boolean,
  ) => void;
}

const OrdersRecoveryTable: React.FC<OrdersRecoveryTableProps> = ({
  data,
  columnsData,
  setShowPagination,
  searchText,
  isLoading,
  isFilterApplied,
  isRecoveriesDetailsLoading,
  isRecoveriesDetailsError,
  handleRecoveryRowExpand,
}) => {
  const dispatch = useDispatch<AppDispatch>();
  const configData = useSelector(
    (state: RootState) => state.CTDBSkuConfigSlice.data,
  );
  const tableRef = useRef<HTMLTableElement>(null);
  const tableScrollRef = useRef<HTMLDivElement>(null);
  const [fixedHeader, setFixedHeader] = useState(false);
  const [columnWidths, setColumnWidths] = useState<number[]>([]);

  useEffect(() => {
    const handleScroll = () => {
      if (!tableRef.current) return;
      const tableTop = tableRef.current.getBoundingClientRect().top;
      setFixedHeader(tableTop < 0);
    };
    window.addEventListener("scroll", handleScroll);
    window.addEventListener("resize", handleScroll);
    return () => {
      window.removeEventListener("scroll", handleScroll);
      window.removeEventListener("resize", handleScroll);
    };
  }, []);

  useEffect(() => {
    if (!tableRef.current) return;
    const firstRow = tableRef.current.querySelector("tr");
    if (!firstRow) return;
    const widths: number[] = Array.from(firstRow.children).map(
      (cell: any) => cell.getBoundingClientRect().width,
    );
    setColumnWidths(widths);
  }, [columnsData]);

  const syncScroll = () => {
    if (tableScrollRef.current) {
      const scrollLeft = tableScrollRef.current.scrollLeft;
      const fixedHeaderTable =
        document.querySelector<HTMLTableElement>(".fixed-header");
      if (fixedHeaderTable) {
        fixedHeaderTable.style.transform = `translateX(-${scrollLeft}px)`;
      }
    }
  };

  useEffect(() => {
    // Only show pagination if there is data
    if (Array.isArray(data) && data.length > 0) {
      setShowPagination(true);
    } else {
      setShowPagination(false);
    }
  }, [data, setShowPagination]);

  if (isLoading) {
    return <Loader />;
  }

  const renderNoSearchResult = () => {
    if (isFilterApplied) {
      const header = searchText
        ? `No result under the filter and by the keyword "${searchText}"`
        : `No result under the filter`;

      const message = searchText
        ? "Did you not find any results? We recommend refining your search criteria and/or broadening your filter parameters to enhance your search outcomes."
        : "Did not find any results? We recommend broadening your filter criteria for a more comprehensive search.";

      return (
        <NoSearchResult
          searchValue={searchText}
          header={header}
          message={message}
        />
      );
    } else {
      return <NoSearchResult searchValue={searchText} />;
    }
  };

  if (Array.isArray(data) && data?.length === 0) {
    const hasEmoji = containsEmoji(searchText);
    if ((!searchText && !isFilterApplied) || hasEmoji) {
      return (
        <EmptyOrder
          image={orderEmptyImg}
          header={NO_DRAFT_RECOVERY_HEADING}
          subHeader={NO_RECOVERY_TEXT}
        />
      );
    }
    return renderNoSearchResult();
  } else {
    if (configData === null && data && data.length > 0) {
      dispatch(fetchSkuConfigData());
    }

    if (!data) {
      return <Loader />;
    }

    const getClassNamesRecoveryTable = (key: string) => {
      switch (key) {
        case "SkuId":
          return "recovery-sku-id";
        case "OrderName":
          return "recovery-order-name";
        case "Amount":
          return "recovery-amount text-right-align";
        case "TransactionDate":
          return "recovery-transaction-date";
        case "BillingType":
          return "recovery-billing-type";
        case "Increment":
          return "recovery-increment";
        case "EngagementId":
          return "recovery-engagement-id";
        default:
          return "";
      }
    };
    return (
      <div
        className="recovery-table-container"
        style={{ position: "relative" }}
      >
        {fixedHeader && (
          <div
            className="fixed-header-wrapper"
            style={{
              overflow: "hidden",
              width: tableScrollRef.current?.getBoundingClientRect().width,
            }}
          >
            <table
              className="motif-table-standard fixed-header"
              style={{ width: columnWidths.reduce((a, b) => a + b, 0) }}
            >
              {" "}
              <thead>
                <tr>
                  {columnsData.map((col, index) => (
                    <th
                      key={col.key}
                      className={getClassNamesRecoveryTable(col.key)}
                      style={{ width: columnWidths[index] }}
                    >
                      {col.label}
                    </th>
                  ))}
                </tr>
              </thead>
            </table>
          </div>
        )}
        <div
          className="table-scroll-wrapper"
          ref={tableScrollRef}
          style={{ overflowX: "auto" }}
          onScroll={syncScroll}
        >
          <table className="motif-table-standard" ref={tableRef}>
            <thead>
              <tr>
                {columnsData.map((col) => (
                  <th
                    key={col.key}
                    className={getClassNamesRecoveryTable(col.key)}
                    style={{ minWidth: "160px" }}
                  >
                    {col.label}{" "}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {data?.map((order) => {
                return (
                  <>
                    <tr>
                      {columnsData.map((col) => (
                        <td key={col.key}>
                          {col.render ? col.render(order) : order[col.key]}
                        </td>
                      ))}
                    </tr>
                    {order.isRecoveryExpanded &&
                      (!isRecoveriesDetailsLoading ? (
                        isRecoveriesDetailsError ? (
                          <tr className="recovery-details-wrapper">
                            <td colSpan={8} style={{ textAlign: "center" }}>
                              <div className="recovery-details recovery-details-error">
                                <div className="home-empty-order-page-default empty-retry">
                                  <div>
                                    <img
                                      src={productListEmptyImg}
                                      alt="Empty Product List"
                                    />
                                  </div>
                                  <div className="empty-order-page-header">
                                    {ERROR_RETRIVING_DATA}
                                  </div>
                                  <div>
                                    <MotifButton
                                      className="refresh-button"
                                      onClick={() =>
                                        handleRecoveryRowExpand(
                                          order.SkuId,
                                          order.OrderParentId,
                                          true,
                                        )
                                      }
                                    >
                                      {TRY_AGAIN}
                                    </MotifButton>
                                  </div>
                                </div>
                              </div>
                            </td>
                          </tr>
                        ) : (
                          order.responseDetails &&
                          Array.isArray(order.responseDetails) && (
                            <tr className="recovery-details-wrapper">
                              <td colSpan={8}>
                                <div
                                  className="recovery-list-content recovery-details"
                                  style={{ marginTop: 12 }}
                                >
                                  <table className="motif-table-standard">
                                    <thead>
                                      <tr>
                                        <th>Transaction Date</th>
                                        <th>Increment Processed</th>
                                        <th>Amount</th>
                                        <th>Engagement Name/ID</th>
                                      </tr>
                                    </thead>
                                    <tbody>
                                      {order.responseDetails.length > 0 ? (
                                        order.responseDetails.map(
                                          (txn: any, idx: number) => (
                                            <tr key={idx}>
                                              <td>
                                                <div>
                                                  <MotifTruncate
                                                    maxLines={1}
                                                    maxWidth={200}
                                                  >
                                                    {txn.TransactionDate ||
                                                      "--"}
                                                  </MotifTruncate>
                                                </div>
                                              </td>
                                              <td>
                                                <div>
                                                  {txn.UsageInterval || "--"}
                                                </div>
                                              </td>
                                              <td>
                                                <div className="order-total">
                                                  {txn.TotalCost || "--"}
                                                </div>
                                              </td>
                                              <td>
                                                <div>
                                                  <MotifTruncate
                                                    maxLines={1}
                                                    maxWidth={200}
                                                  >
                                                    {(txn.EngagementName ||
                                                      "--") +
                                                      (txn.EngagementId
                                                        ? ` (${txn.EngagementId})`
                                                        : "")}
                                                  </MotifTruncate>
                                                </div>
                                              </td>
                                            </tr>
                                          ),
                                        )
                                      ) : (
                                        <tr className="recovery-details-wrapper">
                                          <td
                                            colSpan={8}
                                            style={{ textAlign: "center" }}
                                          >
                                            <div className="recovery-details recovery-details-error">
                                              <div className="home-empty-order-page-default  empty-data">
                                                <div>
                                                  <img
                                                    src={productListEmptyImg}
                                                    alt="Empty Product List"
                                                  />
                                                </div>
                                                <div className="empty-order-page-header">
                                                  {NO_DATA_FOR_SKU}
                                                </div>
                                              </div>
                                            </div>
                                          </td>
                                        </tr>
                                      )}
                                    </tbody>
                                  </table>
                                </div>
                              </td>
                            </tr>
                          )
                        )
                      ) : (
                        <tr className="recovery-details-wrapper">
                          <td colSpan={8} style={{ textAlign: "center" }}>
                            <div className="recovery-details recovery-details-loader">
                              <Loader />
                            </div>
                          </td>
                        </tr>
                      ))}
                  </>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    );
  }
};

export default OrdersRecoveryTable;
